"use client";

import { useCallback, useMemo } from "react";
import type { MeiliThought } from "@/generated/fastapi";
import { showErrorToast, showSuccessToast } from "@/shared/feedback";
import type { ContentItemAction } from "@/shared/ui/display/ContentItemPrimitives";
import type { GenericIconType } from "@/shared/ui/actions/GenericIcon";
import useThoughtActions from "./useThoughtActions";

export type ThoughtItemAction = {
  onClick: (thought: MeiliThought) => void | Promise<void>;
  icon: GenericIconType;
  humanReadable: string;
  actionDescription: string;
  color?: string;
};

export type ResolvedThoughtAction = ContentItemAction;

type UseThoughtItemActionsParams = {
  anchorAction?: ThoughtItemAction;
  isEditing?: boolean;
  onDeleteSearchResultAction?: (thoughtId: string) => void;
  onEditAction: () => void;
  onReflectAction?: (thought: MeiliThought) => void | Promise<void>;
  onTraceOriginAction?: () => void | Promise<void>;
  thought: MeiliThought;
  thoughtText: string;
};

export default function useThoughtItemActions({
  anchorAction,
  isEditing = false,
  onDeleteSearchResultAction,
  onEditAction,
  onReflectAction,
  onTraceOriginAction,
  thought,
  thoughtText,
}: UseThoughtItemActionsParams): ResolvedThoughtAction[] {
  const { handleThoughtsDeleteClick } = useThoughtActions();

  const handleDeleteSearchResult = useCallback(
    (deletedThoughtId: string) => {
      onDeleteSearchResultAction?.(deletedThoughtId);
    },
    [onDeleteSearchResultAction],
  );

  const handleDelete = useCallback(async () => {
    await handleThoughtsDeleteClick(
      {
        thought_ids: [thought.objectID],
        delete_mode: "soft",
        sync_meili_index: "thoughts",
      },
      () => handleDeleteSearchResult(thought.objectID),
      [thought.prototype],
    );
  }, [
    handleDeleteSearchResult,
    handleThoughtsDeleteClick,
    thought.objectID,
    thought.prototype,
  ]);

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(thoughtText);
      showSuccessToast("Copied to clipboard");
    } catch {
      showErrorToast("Failed to copy thought");
    }
  }, [thoughtText]);

  return useMemo<ResolvedThoughtAction[]>(() => {
    if (isEditing) {
      return [];
    }

    return [
      ...(onReflectAction
        ? [
            {
              key: "reflect",
              label: "Reflect",
              humanReadable: "Reflect on this thought",
              icon: "reflect_thoughts" as GenericIconType,
              onClick: () => onReflectAction(thought),
              color: "moveon.primary",
              emphasized: true,
            },
          ]
        : []),
      ...(anchorAction
        ? [
            {
              key: "anchor",
              label: anchorAction.actionDescription,
              humanReadable: anchorAction.humanReadable,
              icon: anchorAction.icon,
              onClick: () => anchorAction.onClick(thought),
              color: anchorAction.color,
            },
          ]
        : []),
      ...(thought.origin_id && onTraceOriginAction
        ? [
            {
              key: "trace_origin",
              label: "Source",
              humanReadable: "Show source material this thought came from",
              icon: "origin_source" as GenericIconType,
              onClick: onTraceOriginAction,
            },
          ]
        : []),
      {
        key: "edit",
        label: "Edit thought",
        humanReadable: "Edit thought",
        icon: "edit" as GenericIconType,
        onClick: onEditAction,
      },
      {
        key: "copy",
        label: "Copy thought",
        humanReadable: "Copy thought",
        icon: "copy_clipboard" as GenericIconType,
        onClick: handleCopy,
      },
      {
        key: "delete",
        label: "Delete thought",
        humanReadable: "Delete thought",
        icon: "delete_soft" as GenericIconType,
        onClick: handleDelete,
        tone: "danger",
      },
    ];
  }, [
    anchorAction,
    handleCopy,
    handleDelete,
    isEditing,
    onEditAction,
    onReflectAction,
    onTraceOriginAction,
    thought,
  ]);
}
